<script lang="ts" setup>
import { onMounted, ref } from "vue";
import { usePrompts } from "@/stores/prompts";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { useRoute, useRouter } from "vue-router";
import { useMateTrait } from "@/stores/mateTrait";
import { useI18n } from "vue-i18n";
import { formatStyle, initStyleWithInitData } from "./Tabs/initStyle";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { useTask } from "@/stores/task";
import { useGlobalModal } from "@/stores/globalModal";

const promptStore = usePrompts();
const store = useMateTrait();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const taskStore = useTask();
// States

const step = ref<string>("prompt");
const isEdit = ref(false);

// prompts
const loading = ref(false);

const reqData = ref<MateTrait>({
  name: "",
  prompt: "",
  negativePrompt: "",
  style: 0,
  mixStyle: [],
  customStyle: { version: 1, prompt: "" },
  mode: 1,
});

// EventHandler
const isGenerating = ref(false);
const imgLoaded = ref(true);
const clickGenerateImg = async () => {
  if (isGenerating.value || !imgLoaded.value) return;
  if (showPrompt.value && reqData.value.prompt.trim().length === 0)
    return showFailToast({
      message: t("Message.promptNotBeenFilledYet"),
    });
  if (!showPrompt.value && showImg.value && !reqData.value.sourceId) {
    return showFailToast({
      message: t("Message.sourceNotBeenFilledYet"),
    });
  }
  const loadingToast = showLoadingToast({
    duration: 0,
    message: t("Message.preparingImage"),
  });
  try {
    const params = {
      ...formatStyle({ ...reqData.value }),
      mateTraitId: Number(route.query.id) || undefined,
    };
    params.from = "trait";
    await taskStore.createTask(params);
  } catch (e) {
    console.log(e);
  } finally {
    loadingToast.close();
    isGenerating.value = false;
  }
};
const clickSave = async () => {
  const { id } = route.query;
  const { prompt, negativePrompt, name } = reqData.value;
  const params: any = {
    name,
    prompt,
    negativePrompt,
  };
  formatStyle({ ...reqData.value }, params);
  const loading = showLoadingToast({
    message: t("Message.saving"),
    duration: 0,
  });
  await store.updateTrait(Number(id), params);
  loading.close();
  showSuccessToast({ message: t("Message.saveSuccess") });
  router.back();
};

// preset
const showPresetModal = ref(false);
const showPrompt = ref(true);
const showImg = ref(true);

const onPresetConfirm = async (preset: PresetMatePrompt) => {
  reqData.value = {
    ...reqData.value,
    prompt: preset.prompt,
    customStyle: {
      version: 2,
      prompt: "",
    },
  };

  const loading = showLoadingToast({
    message: t("Message.saving"),
    duration: 0,
  });
  const params: any = {
    name: preset.name,
    mediaId: preset.avatarMediaId,
    prompt: preset.prompt,
    negativePrompt: "",
    customStyle: {
      version: 2,
      prompt: "",
    },
    mode: 7,
  };
  const res = await store.createTrait(params);
  sessionStorageSet("createTrait", res);
  showSuccessToast({ message: t("Message.saveSuccess") });
  loading.close();
  router.back();
};

// 能量块消耗
const showNormalConfirmModal = ref(false);

onMounted(async () => {
  loading.value = true;
  let {
    id,
    prompt,
    negativePrompt,
    style,
    sourceId,
    sourceUrl,
    mode,
    customStyle,
    mixStyle,
  } = route.query;
  if (mode === "7") {
    // 预设
    showPresetModal.value = true;
  } else if (mode === "9") {
    // 图生特征
    showPrompt.value = false;
    showImg.value = true;
  } else if (mode === "8") {
    // 文生特征
    showPrompt.value = true;
    showImg.value = false;
  } else {
    // 混合模式
    showPrompt.value = true;
    showImg.value = true;
    mode = "1";
  }
  await promptStore.getOrFetchImgPrompts();
  isEdit.value = !!id;
  if (id || prompt || sourceId) {
    // 有id，说明是编辑
    const trait = id
      ? await store.getOrFetchTraitItem(Number(id))
      : ({
          prompt: decodeURIComponent(prompt as string),
          negativePrompt: negativePrompt
            ? decodeURIComponent(negativePrompt as string)
            : "",
          style: style ? Number(style) : undefined,
          sourceId: sourceId ? Number(sourceId) : undefined,
          source: sourceId
            ? {
                id: Number(sourceId),
                url: decodeURIComponent(sourceUrl as string),
              }
            : undefined,
          customStyle: customStyle
            ? JSON.parse(decodeURIComponent(customStyle as string))
            : undefined,
          mixStyle: mixStyle
            ? JSON.parse(decodeURIComponent(mixStyle as string))
            : undefined,
        } as MateTrait);
    reqData.value = {
      ...trait,
      ...initStyleWithInitData(trait.style, trait.customStyle, trait.mixStyle),
      sourceId: trait.sourceId || undefined,
      mode: Number(mode),
    };
  }
  reqData.value.mode = Number(mode);
  loading.value = false;

  useGlobalModal().openServerUpgradingModal();
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr] overflow-hidden">
    <ThreadsNav show-back :show-center="false">
      <template #right>
        <button
          v-if="route.query.id"
          class="btn btn-primary btn-sm rounded"
          @click="clickSave"
        >
          {{ $t("Buttons.save") }}
        </button>
        <button
          class="btn btn-primary btn-sm rounded"
          @click="showNormalConfirmModal = true"
        >
          <i class="iconify mingcute--pic-line"></i>
          {{ $t("Buttons.generate") }}
        </button>
      </template>
    </ThreadsNav>
    <div v-if="loading" class="w-full h-full flex justify-center items-center">
      <div class="loading loading-ring loading-lg"></div>
    </div>
    <div v-else class="flex justify-center overflow-auto">
      <VanTabs
        class="w-full overflow-hidden grid-rows-[auto_1fr] grid h-full"
        :swipe-threshold="0"
        v-model:active="step"
      >
        <VanTab
          v-if="showPrompt"
          :title="$t('trait.tab.descriptors')"
          name="prompt"
        >
          <EditPrompts
            mode="character"
            v-model:prompt="reqData.prompt"
            v-model:negative-prompt="reqData.negativePrompt"
            show-feature-parse
          />
        </VanTab>
        <VanTab v-if="showImg" :title="$t('trait.tab.img')" name="img">
          <TraitTabsLayout is-origin :req-data="reqData" />
        </VanTab>
        <VanTab :title="$t('trait.tab.artStyle')" name="style">
          <ImageTabsStyle
            v-model:style="reqData.style"
            v-model:custom-style="reqData.customStyle"
            v-model:mix-style="reqData.mixStyle"
          />
        </VanTab>
      </VanTabs>
    </div>
  </div>

  <GenerationTraitSelectPresetModal
    v-model:show="showPresetModal"
    @confirm="onPresetConfirm"
  />

  <!-- 能量块消耗 -->
  <ImageModalPowerCubeConfirm
    v-model:show="showNormalConfirmModal"
    :req-data="reqData"
    @generate="clickGenerateImg"
  />
</template>

<style scoped>
:deep(.van-tabs__content) {
  overflow: hidden;
}
:deep(.van-tabs__content > .van-tab__panel) {
  overflow: auto;
}
</style>
